import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from '@/features/snackbar';
import { mutationFn } from './mutationFn';
import { useNarrative } from '../useNarrative';
import type { ResolvedQueryData } from '@/utilities/genericTypes';
import { useUserVotedNarratives } from '../useUserVotedNarratives';

export const useVoteOnNarrative = (slug: string, isUpVoted: boolean) => {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();

  return useMutation({
    mutationFn,
    onSuccess: (response, { narrativeId }) => {
      queryClient.setQueryData<ResolvedQueryData<typeof useNarrative>>(
        useNarrative.getOptions({ slug, ref: slug }).queryKey,
        (state) => {
          if (typeof state === 'undefined') return state;
          return {
            ...state,
            narrative: {
              ...state.narrative,
              voteCount: {
                upvotes:
                  response.narrative?.voteCount.upvotes ??
                  state.narrative.voteCount.upvotes,
              },
            },
          };
        }
      );

      queryClient.setQueryData<
        ResolvedQueryData<typeof useUserVotedNarratives>
      >(useUserVotedNarratives.getOptions({}).queryKey, (state) => {
        if (typeof state !== 'undefined' && state.UserVotedNarratives) {
          return {
            ...state,
            UserVotedNarratives: isUpVoted
              ? state.UserVotedNarratives.filter(
                  (n) => n.narrativeId !== narrativeId
                )
              : [...state.UserVotedNarratives, { narrativeId }],
          };
        }
        return state;
      });
    },
    onError: () => {
      addSnackbar({
        type: 'negative',
        lifeTime: 2000,
        message: `Failed to ${isUpVoted ? 'down' : 'up'}voted narrative`,
      });
    },
  });
};
